import { transformBlogForFrontend } from "@/platforms/customer/services/blogService";

export const useBlogDetailUtils = () => {
  /** Blog detail URL is `/blog/{segment}` — read from pathname (reliable with ":" in id; RR7 `params['*']` can be empty). */
  function blogKeyFromPathname(pathname) {
    const p = String(pathname || "");
    if (!p.startsWith("/blog/")) return "";
    const rest = p.slice("/blog/".length);
    if (!rest) return "";
    try {
      return decodeURIComponent(rest);
    } catch {
      return rest;
    }
  }

  function ensureHttpHref(raw) {
    const u = String(raw || "").trim();
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith("//")) return `https:${u}`;
    return `https://${u.replace(/^\/+/, "")}`;
  }

  /**
   * Sirf woh case jahan value itni chhoti ho ke `https://x` jaisa bekaar host ban jaye — icon mat dikhao.
   * Asal domain / link (dot wala, ya lambi string) par icon dikhao.
   */
  function isJunkOneLetterHost(href) {
    if (!href) return true;
    try {
      const u = new URL(href);
      const h = u.hostname;
      if (h.includes(".")) return false;
      if (h === "localhost") return false;
      return h.length <= 2;
    } catch {
      return true;
    }
  }

  /** If detail fetch parsed social_links to {} but list had real links, keep list. */
  function socialLinksPayloadLooksEmpty(links) {
    if (links == null) return true;
    if (typeof links === "string") return !String(links).trim();
    if (typeof links !== "object" || Array.isArray(links)) return true;
    return !Object.values(links).some((v) => v != null && String(v).trim());
  }

  function faqsPayloadLooksEmpty(faqs) {
    if (faqs == null) return true;
    if (Array.isArray(faqs)) return faqs.length === 0;
    if (typeof faqs === "string") return !String(faqs).trim();
    if (typeof faqs === "object") return Object.keys(faqs).length === 0;
    return true;
  }

  /** Detail fetch kabhi khaali social/FAQs de, list response same post ke liyye join se bharra ho — dono mika do. */
  function enrichRawBlogFromPublicList(rawBlog, listBlogs, blogKey) {
    if (!rawBlog || !Array.isArray(listBlogs) || !listBlogs.length)
      return rawBlog;
    const k = String(blogKey || "")
      .trim()
      .toLowerCase();
    const id = rawBlog.id != null ? String(rawBlog.id).trim() : "";
    const slug =
      rawBlog.slug != null ? String(rawBlog.slug).trim().toLowerCase() : "";
    let match =
      (id && listBlogs.find((x) => String(x?.id || "").trim() === id)) ||
      (slug &&
        listBlogs.find(
          (x) =>
            String(x?.slug || "")
              .trim()
              .toLowerCase() === slug,
        )) ||
      null;
    if (!match && k) {
      for (const x of listBlogs) {
        const bid = x?.id != null ? String(x.id).trim() : "";
        if (bid && bid === k) {
          match = x;
          break;
        }
        if (
          k &&
          !k.includes(":") &&
          bid &&
          bid.includes(":") &&
          k === bid.split(":").pop()
        ) {
          match = x;
          break;
        }
      }
    }
    if (!match) return rawBlog;
    let next = { ...rawBlog };
    // Detail kabhi `writer_id` na bheje; public list hamesha join se `writer_id` deta hai — social-links fetch ke liyye.
    if (next.writer_id == null && match.writer_id != null) {
      next = { ...next, writer_id: match.writer_id };
    }
    if (
      socialLinksPayloadLooksEmpty(next.social_links) &&
      !socialLinksPayloadLooksEmpty(match.social_links)
    ) {
      next = { ...next, social_links: match.social_links };
    }
    if (
      faqsPayloadLooksEmpty(next.faqs) &&
      !faqsPayloadLooksEmpty(match.faqs)
    ) {
      next = { ...next, faqs: match.faqs };
    }
    if (faqsPayloadLooksEmpty(next.faq) && !faqsPayloadLooksEmpty(match.faq)) {
      next = { ...next, faq: match.faq };
    }
    return next;
  }

  function socialLinksObjectEmpty(sl) {
    if (sl == null) return true;
    if (typeof sl !== "object" || Array.isArray(sl)) return true;
    return !Object.values(sl).some((v) => v != null && String(v).trim());
  }

  /** Public blog id is "writerUuid:postUuid" — writer_id fetch ke liyye jab API `writer_id` na bheje. */
  function resolveWriterIdForSocialFetch(b) {
    if (!b) return "";
    const w = b.writerId != null ? String(b.writerId).trim() : "";
    if (w) return w;
    const composite = b.id != null ? String(b.id).trim() : "";
    if (composite.includes(":")) {
      const part = composite.split(":")[0].trim();
      if (part) return part;
    }
    return "";
  }

  /** Writer UUID — optional extra API; icons/links can still come from `social_links` on the post without this. */
  function extractWriterIdForSocialApi(rawBlog, b) {
    const fromDisplay = resolveWriterIdForSocialFetch(b);
    if (fromDisplay) return fromDisplay;
    if (rawBlog && rawBlog.writer_id != null) {
      const t = String(rawBlog.writer_id).trim();
      if (t) return t;
    }
    const rawId = rawBlog?.id != null ? String(rawBlog.id).trim() : "";
    if (rawId.includes(":")) {
      const w = rawId.split(":")[0].trim();
      if (w) return w;
    }
    return "";
  }

  /**
   * Jis row se `author` naam aata hai usi list item mein `social_links` hota hai; detail/local kabhi
   * naam de de lekin social drop ho — transform ke baad public list se dobara bharo.
   */
  function findListRowForDisplayBlog(b, listBlogs, blogKey) {
    if (!b || !Array.isArray(listBlogs) || !listBlogs.length) return null;
    const id = b.id != null ? String(b.id).trim() : "";
    const slugKey = (b.slug != null ? String(b.slug).trim() : id).toLowerCase();
    const k = String(blogKey || "")
      .trim()
      .toLowerCase();

    let match =
      (id && listBlogs.find((x) => String(x?.id || "").trim() === id)) ||
      (slugKey &&
        listBlogs.find(
          (x) =>
            String(x?.slug || "")
              .trim()
              .toLowerCase() === slugKey,
        )) ||
      (k && listBlogs.find((x) => String(x?.id || "").trim() === k)) ||
      null;

    if (!match && k && !k.includes(":")) {
      match =
        listBlogs.find((x) => {
          const bid = String(x?.id || "");
          return bid.includes(":") && bid.split(":").pop() === k;
        }) || null;
    }

    if (!match && b?.title && b?.author) {
      const t = String(b.title).trim().toLowerCase();
      const a = String(b.author).trim().toLowerCase();
      match =
        listBlogs.find(
          (x) =>
            String(x?.title || "")
              .trim()
              .toLowerCase() === t &&
            String(x?.author || "")
              .trim()
              .toLowerCase() === a,
        ) || null;
    }
    return match;
  }

  function patchDisplayBlogFromListRow(b, listBlogs, blogKey) {
    if (!b || !listBlogs?.length) return b;
    const row = findListRowForDisplayBlog(b, listBlogs, blogKey);
    if (!row) return b;
    const patched = transformBlogForFrontend(row);
    if (!patched) return b;
    let out = b;
    if (!out.writerId && patched.writerId) {
      out = { ...out, writerId: patched.writerId };
    }
    if (
      socialLinksObjectEmpty(out.socialLinks) &&
      !socialLinksObjectEmpty(patched.socialLinks)
    ) {
      out = { ...out, socialLinks: patched.socialLinks };
    }
    if (!Array.isArray(out.faqs) || out.faqs.length === 0) {
      if (Array.isArray(patched.faqs) && patched.faqs.length > 0) {
        out = { ...out, faqs: patched.faqs };
      }
    }
    return out;
  }

  function normalizeHtmlLinks(html) {
    const raw = String(html || "");
    if (!raw) return "";
    if (typeof document === "undefined") return raw;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(raw, "text/html");
      const anchors = Array.from(doc.querySelectorAll("a[href]"));
      for (const a of anchors) {
        const href = String(a.getAttribute("href") || "").trim();
        if (!href) continue;
        const isHash = href.startsWith("#");
        const isMailOrTelOrJs =
          href.startsWith("mailto:") ||
          href.startsWith("tel:") ||
          href.startsWith("javascript:");

        // Hash / mail / tel / js links: keep same-tab behavior
        if (isHash || isMailOrTelOrJs) {
          a.setAttribute("target", "_self");
          a.removeAttribute("rel");
          continue;
        }

        const origin =
          typeof window !== "undefined"
            ? String(window.location?.origin || "")
            : "";
        const base = origin || "https://www.tripsadora.com";

        // Resolve ANY relative-ish hrefs ("/x", "x", "./x", "../x") against the site base.
        // This prevents broken URLs like "https://../tours/...".
        let resolved;
        try {
          resolved = new URL(href, base);
        } catch {
          resolved = null;
        }

        // If we can resolve and it's same-origin, open in same tab.
        if (resolved && resolved.origin === base.replace(/\/+$/, "")) {
          a.setAttribute("href", resolved.href);
          a.setAttribute("target", "_self");
          a.removeAttribute("rel");
          continue;
        }

        // Otherwise treat as external: normalize protocol and open new tab.
        if (resolved) {
          a.setAttribute("href", resolved.href);
        } else if (href.startsWith("//")) {
          a.setAttribute("href", `https:${href}`);
        } else if (!/^https?:\/\//i.test(href)) {
          a.setAttribute("href", `https://${href.replace(/^\/+/, "")}`);
        }
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      }
      return doc.body.innerHTML;
    } catch {
      return raw;
    }
  }

  function getInitials(name) {
    const s = String(name || "").trim();
    if (!s) return "?";
    const parts = s.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || "";
    const b = parts[1]?.[0] || "";
    return (a + b).toUpperCase() || s.slice(0, 1).toUpperCase();
  }

  function slugifyHeadingId(text) {
    const s = String(text || "")
      .trim()
      .toLowerCase()
      .replace(/['"]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
    return s || "section";
  }

  function blogSortTimestamp(b) {
    if (!b || typeof b !== "object") return 0;
    const candidates = [
      b.publishedAt,
      b.publish_date,
      b.createdAt,
      b.updatedAt,
      b.updated_date,
      b.date,
    ];
    for (const c of candidates) {
      if (c == null) continue;
      const s = String(c).trim();
      if (!s) continue;
      const ms = Date.parse(s);
      if (!Number.isNaN(ms)) return ms;
    }
    return 0;
  }

  function truncateWithEllipsis(text, maxChars) {
    const s = String(text || "");
    const n = Number(maxChars) || 0;
    if (n <= 0) return "";
    if (s.length <= n) return s;
    return `${s.slice(0, Math.max(0, n)).trimEnd()}…`;
  }

  function buildTocAndHtml(html) {
    const raw = String(html || "");
    if (!raw) return { toc: [], htmlWithIds: "" };
    if (typeof document === "undefined") return { toc: [], htmlWithIds: raw };

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(raw, "text/html");

      const headings = Array.from(doc.querySelectorAll("h2, h3")).filter((h) =>
        String(h.textContent || "").trim(),
      );

      const toc = [];
      let idx = 0;
      const usedIds = new Set();
      for (const h of headings) {
        if (h.tagName.toLowerCase() !== "h2") continue; // match screenshot style: main sections
        idx += 1;
        const title = String(h.textContent || "").trim();
        // Pull a short description from the first paragraph-ish element after the heading.
        // This is used by the TOC “right column” in the screenshot.
        let description = "";
        try {
          let next = h.nextElementSibling;
          while (next && /^(br|hr)$/i.test(String(next.tagName || "")))
            next = next.nextElementSibling;
          while (
            next &&
            /^(div|span)$/i.test(String(next.tagName || "")) &&
            !String(next.textContent || "").trim()
          ) {
            next = next.nextElementSibling;
          }
          const txt = next
            ? String(next.textContent || "")
                .replace(/\s+/g, " ")
                .trim()
            : "";
          if (txt)
            description = txt.length > 140 ? `${txt.slice(0, 139)}…` : txt;
        } catch {
          description = "";
        }
        let id = String(h.getAttribute("id") || "").trim();
        if (!id) {
          let baseId = slugifyHeadingId(title);
          id = baseId;
          let n = 2;
          while (usedIds.has(id)) {
            id = `${baseId}-${n}`;
            n += 1;
          }
          h.setAttribute("id", id);
        }
        usedIds.add(id);
        toc.push({ n: idx, title, id, description });
      }

      return { toc, htmlWithIds: doc.body.innerHTML };
    } catch {
      return { toc: [], htmlWithIds: raw };
    }
  }

  return {
    blogKeyFromPathname,
    ensureHttpHref,
    isJunkOneLetterHost,
    socialLinksPayloadLooksEmpty,
    faqsPayloadLooksEmpty,
    enrichRawBlogFromPublicList,
    socialLinksObjectEmpty,
    extractWriterIdForSocialApi,
    patchDisplayBlogFromListRow,
    normalizeHtmlLinks,
    getInitials,
    blogSortTimestamp,
    truncateWithEllipsis,
    buildTocAndHtml,
  };
};
